import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = sessionStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const login = useCallback(async (username, password, deviceToken = null) => {
    const response = await api.post('/auth/login', {
      username,
      password,
      device_token: deviceToken,
    });
    const data = response.data;

    // Handle intermediate states — return them to the caller to route appropriately.
    // No tokens are issued yet in these cases.
    if (data.requires_password_change || data.requires_mfa || data.requires_mfa_enrollment) {
      return data;
    }

    // Normal login — store session and update user state
    const { access_token, refresh_token, user: userData } = data;
    sessionStorage.setItem('access_token', access_token);
    sessionStorage.setItem('refresh_token', refresh_token);
    sessionStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    return userData;
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = sessionStorage.getItem('refresh_token');
    try {
      if (refreshToken) {
        await api.post('/auth/logout', { refresh_token: refreshToken });
      }
    } catch {
      // Logout silently even if the server call fails
    } finally {
      sessionStorage.clear();
      setUser(null);
    }
  }, []);

  const completeLogin = useCallback((access_token, refresh_token, userData, device_token = null) => {
    sessionStorage.setItem('access_token', access_token);
    sessionStorage.setItem('refresh_token', refresh_token);
    sessionStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    if (device_token) {
      localStorage.setItem('device_token', device_token);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, completeLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}